import * as React from 'react';
import { TextField as MatTextArea, TextFieldProps as MatTextFieldProps, SxProps, Theme } from '@mui/material';
import { useSafeId } from '../../hooks';

interface TextAreaStandardProps extends TextAreaBaseProps {
  variant?: 'standard';
}

interface TextAreaFilledProps extends TextAreaBaseProps {
  variant: 'filled';
}

interface TextAreaOutlinedProps extends TextAreaBaseProps {
  variant: 'outlined';
}

type MarginType = 'dense' | 'normal' | 'none';
export type TextAreaProps = TextAreaOutlinedProps | TextAreaStandardProps | TextAreaFilledProps;

interface TextAreaBaseProps {
  value?: string;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  label?: string;
  multiline?: boolean;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  required?: boolean;
  margin?: MarginType;
  InputProps?: MatTextFieldProps['InputProps'];
  readOnly?: boolean;
  variant?: 'outlined' | 'standard' | 'filled';
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  id?: string;
  autoFocus?: boolean;
  dataProp?: string;
  sx?: SxProps<Theme>;
}
const TextArea: React.FunctionComponent<TextAreaProps> = props => {
  const {
    value,
    disabled,
    error,
    fullWidth,
    helperText,
    label,
    placeholder,
    minRows,
    maxRows,
    required,
    margin,
    variant,
    onChange,
    id,
    InputProps = {},
    readOnly,
    autoFocus,
    dataProp,
    ...rest
  } = props;
  const safeId = useSafeId(id);

  return (
    <MatTextArea
      value={value}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      helperText={helperText}
      label={label}
      placeholder={placeholder}
      minRows={minRows}
      maxRows={maxRows}
      required={required}
      margin={margin}
      variant={variant}
      onChange={onChange}
      id={safeId}
      InputProps={{
        readOnly,
        autoFocus,
        inputProps: { 'data-selector': dataProp ? dataProp : null },
        ...InputProps
      }}
      {...rest}
      multiline
    />
  );
};

TextArea.defaultProps = {
  margin: 'normal',
  fullWidth: true,
  label: '',
  minRows: 4,
  variant: 'filled',
  readOnly: false,
  autoFocus: false
};

TextArea.displayName = 'TextArea';

export { TextArea };
